import { DialogActions, DialogContent, DialogTitle, Menu, MenuItem, Divider, ListItemIcon, Tab, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ControlpanelPrimaryButton, GrayIconButton, WhiteButton } from '../../../components/StyledButton';
import { CustomDialog, CustomTabs, CustomTabsV2 } from '../../../components/StyledComponents';
import { useLoading } from '../../../context/LoadingContext';
import { useAuth } from '../../../context/AuthContext';
import { format } from 'date-fns';
import { updateNotification } from '../../../axios/Notification/NotificationApi';
import { toast } from 'react-toastify';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import React, { useMemo, useState } from 'react';
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { formatInTimeZone } from "date-fns-tz";
import IconNotificationBellBig from '../../../assets/images/icons/IconNotificationBellBig';
import { useNavigate } from "react-router-dom";


export function formatDateWithOffset(dateString, dateTimeFormat, i18n) {
   // 1. Format theo timezone máy
   const localFormatted = formatInTimeZone(
      dateString,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateTimeFormat
   );

   // 2. Lấy offset từ date backend (vd: +0700)
   const rawOffset = new Date(dateString)
      .toString()
      .match(/GMT([+-]\d{4})/)[1];

   const offsetFormatted = rawOffset.slice(0, 3) + ":" + rawOffset.slice(3);

   // 3. Chọn prefix theo ngôn ngữ
   const prefix = i18n.language.startsWith("vi") ? "GMT" : "UTC";

   return `${localFormatted} (${prefix} ${offsetFormatted})`;
}

export const isApproverNotificationRole = (role) =>
   role === "ADMIN" || role === "INCHARGE";

export const isLeaveNotificationType = (type) =>
   type === "ADD_LEAVE" ||
   type === "EDIT_LEAVE" ||
   type === "DELETE_LEAVE" ||
   type === "LEAVE";

export const getLeaveNotificationRequestTitle = (type, t) => {
   if (type === "EDIT_LEAVE") return t("common.leave-history-type-edit");
   if (type === "DELETE_LEAVE") return t("common.leave-history-type-delete");
   return t("common.leave-history-type-add");
};

export const getLeaveNotificationResultTitle = (type, t) => {
   if (type === "EDIT_LEAVE") return t("common.leave-edit-request-result");
   if (type === "DELETE_LEAVE") return t("common.leave-delete-request-result");
   return t("common.leave-add-request-result");
};

export const getLeaveNotificationResultMessage = (type, status, processedName, t) => {
   const approved = status === "APPROVED";

   if (type === "EDIT_LEAVE") {
      return `${t(
         approved
            ? "common.your-request-to-edit-leave-has-been-approve-by"
            : "common.your-request-to-edit-leave-has-been-reject-by"
      )}${processedName}`;
   }

   if (type === "DELETE_LEAVE") {
      return `${t(
         approved
            ? "common.your-request-to-delete-leave-has-been-approve-by"
            : "common.your-request-to-delete-leave-has-been-reject-by"
      )}${processedName}`;
   }

   return `${t(
      approved
         ? "common.your-leave-request-has-been-approve-by"
         : "common.your-leave-request-has-been-reject-by"
   )}${processedName}`;
};

export const getLeaveNotificationPendingContent = (type, requestUserName, t) => {
   if (type === "EDIT_LEAVE") {
      return `${requestUserName}${t("common.content-request-notification-edit-leave")}`;
   }
   if (type === "DELETE_LEAVE") {
      return `${requestUserName}${t("common.content-request-notification-delete-leave")}`;
   }
   return `${t("common.content-request-notification-leave")}${requestUserName}`;
};

const NotificationDialog = ({ onClose, notifications, handleRefreshData, allUser }) => {
   const { t, i18n } = useTranslation();
   const [show, hide] = useLoading();
   const { dateTimeFormat, isAdmin, profile } = useAuth();
   const [anchorEl, setAnchorEl] = useState(null);

   const [tabValue, setTabValue] = useState(0);
   const navigate = useNavigate();

   const handleCloseDialog = () => {
      onClose();
   };

   const refreshData = () => {
      handleRefreshData();
   };

   const handleNavigateByNotification = (notif) => {
      const { id, type, detail, requestId, eventId, eventVisitorId } = notif;

      if (type === "ADD_LEAVE" || type === "EDIT_LEAVE" || type === "DELETE_LEAVE") {
         navigate(`/controlpanel/leave-and-holiday-management?leaveId=${id}`);
         return;
      }

      if (type === "EVENTLOG") {
         navigate(`/controlpanel/event-log-management?requestId=${id}`);
         return;
      }

      if (type === "SWAP_SHIFT") {
         navigate(`/controlpanel/shift-and-roster-management?requestShiftId=${id}&requestId=${requestId}`);
         return;
      }

      if (type === "VISITOR") {
         navigate(`/controlpanel/visitor-management?eventId=${eventId}&eventVisitorId=${eventVisitorId}`);
         return;
      }
   };


   const visibleNotifications = useMemo(() => {
      return notifications;
   }, [notifications]);


   const unreadNotifications = useMemo(
      () => visibleNotifications.filter((n) => n.unread),
      [visibleNotifications]
   );

   const handleUpdateNotificationToRam = async (payload) => {
      try {
         const role = isAdmin ? "ADMIN" : "USER"
         const response = await updateNotification(payload, profile.userId, role, profile.companyId);
      } catch (error) {
         toast.error('Notifications updated in RAM fail');
      }
   };

   const handleRemoveNotification = (id) => {
      const updated = notifications.filter((item) => item.notificationId !== id);
      handleUpdateNotificationToRam(updated)
      handleRefreshData()
   };

   const handleReadAll = () => {
      const updatedNotifications = notifications.map(n => ({
         ...n,
         unread: false
      }));
      handleUpdateNotificationToRam(updatedNotifications)
      handleRefreshData()
      setAnchorEl(null)
   }

   const handleReadNotification = (id) => {
      const updated = notifications.map(n =>
         n.notificationId === id ? { ...n, unread: false } : n
      );
      handleUpdateNotificationToRam(updated)
      handleRefreshData()
   }

   const handleClearAll = () => {
      handleUpdateNotificationToRam([])
      handleRefreshData()
      setAnchorEl(null)
   }

   const handleChangeTab = (event, newValue) => {
      setTabValue(newValue);
   };

   const a11yProps = (index) => {
      return {
         id: `notification-tab-${index}`,
         'aria-controls': `notification-tabpanel-${index}`,
      };
   }

   function TabPanel(props) {
      const { children, value, index, ...other } = props;
      return (
         <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
         >
            {value === index && <div>{children}</div>}
         </div>
      );
   }

   function NotificationItem({ date_request_event, end_date_leave, id, requestId, leave_policy, leave_type, notificationId, request_reason, request_type_id, processed_by_userId,
      response_reason, shift_id, start_date_leave, status, time_zone, type, user_id, dateTimeSendRequest, unread, onNavigate, onClose, detail, role, isAdmin, request_user_id, eventId,
      eventName,
      eventVisitorId,
      visitorName }) {
      const userName = allUser.find(u => u.userId === user_id)?.fullName || "";
      const requestUserName = allUser.find(u => u.userId === request_user_id)?.fullName || "";
      const processed_name = allUser.find(u => u.userId === processed_by_userId)?.fullName || "";
      const [anchorElNotification, setAnchorElNotification] = useState(null);
      const isOwner = user_id === profile.userId;
      const isPending =
         type === "SWAP_SHIFT"
            ? isApproverNotificationRole(role)
            : status === "REQUESTED";

      const isResultView =
         type === "SWAP_SHIFT"
            ? (role === "OWNER" && status === "PENDING") ||
            ["APPROVED", "REJECTED", "PARTNER_REJECTED"].includes(status)
            : isApproverNotificationRole(role)
               ? ["APPROVED", "REJECTED"].includes(status)
               : ["APPROVED", "REJECTED"].includes(status);

      const [selectedId, setSelectedId] = useState(null);

      const handleClickItem = () => {
         setSelectedId(notificationId);
      };

      const handleRowClick = () => {
         // chọn dòng
         setSelectedId(notificationId);
         // nếu chưa đọc thì mark đọc luôn
         if (unread) {
            handleReadNotification(notificationId);
         }

         const shouldNavigate = !(
            type === "SWAP_SHIFT" &&
            status === "PENDING" &&
            !isApproverNotificationRole(role)
         );

         if (shouldNavigate && onNavigate) {
            onNavigate({
               id,
               type,
               requestId,
               date_request_event,
               dateTimeSendRequest,
               eventId,
               eventVisitorId
            });
         }

         if (onClose) {
            onClose();
         }
      };

      return (
         // <div 
         //    // onClick={handleClickItem}
         //    className={`flex items-start gap-3 py-4 px-1 cursor-pointer hover:bg-gray-50`}>
         <div
            onClick={handleRowClick}
            className={`flex items-start gap-3 py-3 px-2 cursor-pointer hover:bg-gray-50 ${selectedId === notificationId ? "bg-[#EFF6FF]" : ""
               }`}
         >
            {unread ? (
               <span className="w-2 h-2 bg-blue-500 rounded-full mt-[5px]"></span>
            ) : (
               <span className="w-2 h-2 invisible"></span>
            )}
            <div className="flex-1">
               {isResultView ? (
                  <>
                     {/* ===== RESULT ===== */}
                     <h4 className="text-sm text-neutral-900 font-semibold">
                        {type === "SWAP_SHIFT"
                           ? t("common.swap-shift-result")
                           : type === "EVENTLOG"
                              ? t("common.event-request-result")
                              : isLeaveNotificationType(type)
                                 ? getLeaveNotificationResultTitle(type, t)
                                 : t("common.attendance-visitor-request")}
                     </h4>

                     <p className="text-xs text-neutral-900 font-normal">
                        {type === "SWAP_SHIFT"
                           ? status === "PENDING" && role === "OWNER"
                              ? `${t("common.your-request-has-been-approved-by")} ${processed_name}`

                              : status === "APPROVED"
                                 ? `${t("common.your-request-has-been-approved-by")} ${processed_name}`

                                 : ["REJECTED", "PARTNER_REJECTED"].includes(status)
                                    ? `${t("common.your-request-has-been-rejected-by")} ${processed_name}`
                                    : ""
                           : type === "EVENTLOG"
                              ? status === "APPROVED"
                                 ? request_type_id === 1
                                    ? `${t("common.your-request-to-add-event-has-been-appove-by")} ${processed_name}`
                                    : `${t("common.your-request-to-edit-event-has-been-appove-by")} ${processed_name}`
                                 : request_type_id === 1
                                    ? `${t("common.your-request-to-add-event-has-been-rejected-by")} ${processed_name}`
                                    : `${t("common.your-request-to-edit-event-has-been-rejected-by")} ${processed_name}`

                              : isLeaveNotificationType(type)
                                 ? getLeaveNotificationResultMessage(type, status, processed_name, t)
                                 : ""}
                     </p>
                  </>
               ) : (
                  <>
                     {/* ===== REQUEST (PENDING) ===== */}
                     <h4 className="text-sm text-neutral-900 font-semibold">
                        {type === "SWAP_SHIFT"
                           ? t("common.request-swap")
                           : type === "EVENTLOG"
                              ? request_type_id === 1
                                 ? t("common.attendance-event-add-request")
                                 : t("common.request-to-edit-an-attendance-event")
                              : isLeaveNotificationType(type)
                                 ? getLeaveNotificationRequestTitle(type, t)
                                 : t("common.attendance-visitor-request")}
                     </h4>

                     <p className="text-xs text-neutral-900 font-normal">
                        {type === "SWAP_SHIFT" && status === "PARTNER_PENDING"
                           ? `${detail?.fullNameFrom} ${t("common.request-swap-with-you")}`

                           : type === "SWAP_SHIFT" && isApproverNotificationRole(role)
                              ? t("common.request-swap-by", {
                                 fromName: detail?.fullNameFrom,
                                 toName: detail?.fullNameTo
                              })

                              : type === "VISITOR"
                              ? `${visitorName} ${t("common.visitor-request-des")} ${eventName}`

                              : type === "EVENTLOG"
                                 ? request_type_id === 1
                                    ? `${requestUserName} ${t("common.content-request-notification-event-check-in")}`
                                    : `${requestUserName} ${t("common.content-request-notification-event-check-out")}`

                                 : isLeaveNotificationType(type)
                                    ? getLeaveNotificationPendingContent(type, requestUserName, t)
                                    : ""}
                     </p>
                  </>
               )}

               <p className="text-10 text-gray-500 font-normal mt-[4px]">
                  {formatDateWithOffset(dateTimeSendRequest, dateTimeFormat, i18n)}
               </p>
            </div>


            {/* <button
               className="p-1 text-gray-400 hover:text-gray-600"
               onClick={(e) => setAnchorElNotification(e.currentTarget)}
            >
               <MoreVertIcon fontSize="small" />
            </button> */}
            <button
               className="p-1 text-gray-400 hover:text-gray-600"
               onClick={(e) => {
                  e.stopPropagation();
                  setAnchorElNotification(e.currentTarget);
               }}
            >
               <MoreVertIcon fontSize="small" />
            </button>

            <Menu
               anchorEl={anchorElNotification}
               open={Boolean(anchorElNotification)}
               onClose={() => setAnchorElNotification(null)}
               anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
               }}
               transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
               }}
               PaperProps={{
                  sx: {
                     border: "1px solid #e5e7eb",
                     borderRadius: "8px",
                     boxShadow: "0px 4px 12px rgba(0,0,0,0.08)"
                  }
               }}
            >

               {unread &&
                  <>
                     <MenuItem
                        onClick={(e) => {
                           e.stopPropagation();          // ⬅️ QUAN TRỌNG
                           handleReadNotification(notificationId);
                           setAnchorElNotification(null);
                        }}
                     >
                        <ListItemIcon>
                           <DoneAllIcon fontSize="small" />
                        </ListItemIcon>
                        {t('common.read')}
                     </MenuItem>
                     <Divider sx={{ mx: 2 }} />
                  </>
               }

               <MenuItem
                  onClick={(e) => {
                     e.stopPropagation();
                     handleRemoveNotification(notificationId);
                     setAnchorElNotification(null);
                  }}
                  sx={{ color: "red" }}
               >
                  <ListItemIcon>
                     <DeleteIcon fontSize="small" sx={{ color: "red" }} />
                  </ListItemIcon>
                  {t('common.clear')}
               </MenuItem>
            </Menu>
         </div>
      );
   }


   return (
      <CustomDialog maxWidth={'xl'} open onClose={handleCloseDialog} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('common.notifications')}</p>
               </div>
               <div className="flex items-center gap-2">
                  <WhiteButton open={Boolean(anchorEl)} onClick={(e) => setAnchorEl(e.currentTarget)}>
                     <MoreHorizIcon />
                  </WhiteButton>
                  <GrayIconButton onClick={handleCloseDialog}>
                     <CloseIcon />
                  </GrayIconButton>
               </div>
            </div>
         </DialogTitle>

         <DialogContent>
            <div className="h-[600px] flex flex-col">
               <div className="sticky top-0 bg-white z-10">
                  <CustomTabsV2
                     variant="fullWidth"
                     indicatorColor="none"
                     value={tabValue}
                     onChange={handleChangeTab}
                  >
                     <Tab
                        label={<p className="text-sm">{t('common.all')}</p>}
                        {...a11yProps(0)}
                        disableRipple
                     />
                     <Tab
                        label={<p className="text-sm">{t('common.unread')}</p>}
                        {...a11yProps(1)}
                        disableRipple
                     />
                  </CustomTabsV2>
               </div>

               <div className="flex-1 overflow-y-auto">
                  {/* TAB ALL */}
                  <TabPanel value={tabValue} index={0}>
                     {visibleNotifications.length === 0 ? (
                        <div className="h-[500px] flex-1 flex flex-col items-center justify-center text-gray-500 py-10">
                           <IconNotificationBellBig className="mb-2 w-8 h-8" />
                           <p className="text-sm font-semibold text-gray-800 mt-4">
                              {t('common.no-notifications')}
                           </p>
                        </div>
                     ) : (
                        visibleNotifications.map((n) => (
                           <React.Fragment key={n.notificationId}>
                              <NotificationItem
                                 {...n}
                                 onNavigate={handleNavigateByNotification}
                                 onClose={handleCloseDialog}
                                 isAdmin={isAdmin}
                              />
                              <div className="h-[1px] bg-gray-200" />
                           </React.Fragment>
                        ))
                     )}
                  </TabPanel>

                  {/* TAB UNREAD */}
                  <TabPanel value={tabValue} index={1}>
                     {unreadNotifications.length === 0 ? (
                        <div className="h-[500px] flex-1 flex flex-col items-center justify-center text-gray-500 py-10">
                           <IconNotificationBellBig className="mb-2 w-8 h-8" />
                           <p className="text-sm font-semibold text-gray-800 mt-4">
                              {t('common.no-notifications')}
                           </p>
                        </div>
                     ) : (
                        unreadNotifications.map((n) => (
                           <React.Fragment key={n.notificationId}>
                              <NotificationItem
                                 {...n}
                                 onNavigate={handleNavigateByNotification}
                                 onClose={handleCloseDialog}
                                 isAdmin={isAdmin}

                              />
                              <div className="h-[1px] bg-gray-200 mx-6" />
                           </React.Fragment>
                        ))
                     )}
                  </TabPanel>
               </div>
            </div>
         </DialogContent>

         <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
               sx: {
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                  marginTop: "5px"
               }
            }}
         >
            <MenuItem onClick={() => { handleReadAll() }} disabled={notifications.length === 0}>
               <ListItemIcon>
                  <DoneAllIcon fontSize="small" />
               </ListItemIcon>
               {t('common.read-all')}
            </MenuItem>
            <Divider sx={{ mx: 2 }} />
            <MenuItem
               onClick={() => { handleClearAll() }}
               sx={{ color: "red" }}
               disabled={notifications.length === 0}
            >
               <ListItemIcon>
                  <DeleteIcon fontSize="small" sx={{ color: "red" }} />
               </ListItemIcon>
               {t('common.clear-all')}
            </MenuItem>
         </Menu>
      </CustomDialog >
   );
};

export default NotificationDialog;